import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '../../components/Link';
import { Eyebrow } from '../../components/Eyebrow';
import { Band } from '../../components/editorial/Band';
import { PageHeader } from '../../components/editorial/PageHeader';
import { Tile } from '../../components/editorial/Tile';
import { TileRow } from '../../components/editorial/TileRow';
import { ProviderArtwork } from '../../components/ProviderArtwork';
import { catalogQueryOptions } from '../../features/catalog/queries';
import {
  providerProfiles,
  type ProviderProfile,
} from '../../features/catalog/providerProfiles';
import { fmtReq } from '../../lib/format';
import { ProviderRail, providerKind } from './ProviderRail';

export interface ProviderDirectoryPageProps {
  initialProfiles: ProviderProfile[];
}

const PASTELS = ['pink', 'teal', 'mint'] as const;

/** What the tile's caption says about a provider, in one sentence. */
const tileSentence = (profile: ProviderProfile): string => {
  const count = profile.endpoints.length;
  const endpoints = `${count} endpoint${count === 1 ? '' : 's'}`;
  return profile.requests > 0
    ? `${providerKind(profile)} behind ${endpoints}, ${fmtReq(profile.requests)} requests in the last 30 days.`
    : `${providerKind(profile)} behind ${endpoints}.`;
};

const FeaturedProvider = ({
  profile,
  pastel,
}: {
  profile: ProviderProfile;
  pastel: (typeof PASTELS)[number];
}) => (
  <Tile
    lead={profile.source.name}
    pastel={pastel}
    artClassName="grid place-items-center"
    art={<ProviderArtwork source={profile.source} size={84} />}
  >
    {tileSentence(profile)}{' '}
    <Link to={`/providers/${profile.source.id}`} className="link-dotted">
      See its traffic
    </Link>
  </Tile>
);

/**
 * The provider directory: who is actually serving the catalog's requests.
 *
 * The three busiest are figures, because a portrait at 84px is the only thing
 * on this page a reader recognises at a glance; everyone else is a rail row,
 * which is what a ranked index of fifty names wants to be. No closing banner:
 * this page is a ledger, and the reader who opens it is checking a fact rather
 * than deciding to buy.
 */
export const ProviderDirectoryPage = ({ initialProfiles }: ProviderDirectoryPageProps) => {
  const entries = useQuery(catalogQueryOptions()).data;
  const profiles = useMemo(
    () => (entries ? providerProfiles(entries) : initialProfiles),
    [entries, initialProfiles],
  );
  const featured = profiles.slice(0, PASTELS.length);
  const rest = profiles.slice(featured.length);

  return (
    <>
      <PageHeader
        eyebrow={<Eyebrow>Provider network</Eyebrow>}
        title={
          <>
            See who is <span className="muted-word">serving the traffic</span>
          </>
        }
        lede="Behind most endpoints the request goes to established providers such as Apify and ScrapeCreators under volume contracts, with smaller specialist sources behind automatic failover. Marketplace providers appear here under stable animal pseudonyms and direct data sources under their own brand. Rankings use successful requests served in the last 30 days."
      />

      <Band>
        {featured.length > 0 ? (
          <div className="band rule-dashed">
            <TileRow columns={3}>
              {featured.map((profile, index) => (
                <FeaturedProvider
                  key={profile.source.id}
                  profile={profile}
                  pastel={PASTELS[index % PASTELS.length]}
                />
              ))}
            </TileRow>
          </div>
        ) : null}
        <div className="band">
          <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="display-3 text-text">
              {featured.length > 0 ? 'The rest of the network' : 'Every provider'}
            </h2>
            <span className="text-13 text-text-3">Traffic ranked</span>
          </div>
          <ProviderRail profiles={rest} />
        </div>
      </Band>
    </>
  );
};
